import { useState } from 'react';
import { useI18n } from '../i18n';
import { findingsEn } from '../i18n/en';
import { fill } from '../i18n';
import { eur, pct } from '../lib/format';
import { getLang } from '../i18n';
import { getAreaId } from '../lib/area';

interface Counts { created: number; updated: number; unchanged: number }
interface Finding { level: 'error' | 'warn' | 'info'; code: string; message: string; params?: Record<string, string | number>; refs: { source: string; row: number }[] }
interface Report {
  dryRun: boolean;
  sources: { name: string; rows: number }[];
  counts: Record<string, Counts>;
  findings: Finding[];
}

const TONE = { error: 'neg', warn: 'crit', info: 'neu' } as const;
const COUNT_KEYS = ['customers', 'projects', 'targets', 'orders', 'orderLines', 'invoices'] as const;
const short = (s: string) => /(20\d{2})/.exec(s)?.[1] ?? s;

/** Deutsch kommt fertig vom Server; für Englisch wird die Vorlage zum Code mit den Werten gefüllt. */
function findingText(f: Finding): string {
  const tpl = getLang() === 'en' ? findingsEn[f.code] : undefined;
  if (!tpl || !f.params) return f.message;
  const p = f.params;
  const billed = Number(p.billed ?? 0);
  const volume = Number(p.volume ?? 0);
  return fill(tpl, { ...p, billed: eur(billed), volume: eur(volume), pct: volume ? pct(billed / volume) : '–', source: short(String(p.source ?? '')) });
}

/** Excel-Import als Reiter der Verwaltung. */
export function ImportPage() {
  const { t } = useI18n();
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<'dry' | 'apply' | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(apply: boolean) {
    setBusy(apply ? 'apply' : 'dry');
    setError(null);
    const body = new FormData();
    files.forEach((f) => body.append('files', f, f.name));
    try {
      const r = await fetch(`/api/admin/import${apply ? '?apply=1' : ''}`, { method: 'POST', body, headers: { 'x-lang': getLang(), ...(getAreaId() ? { 'x-area': getAreaId()! } : {}) } });
      const json = await r.json();
      if (!r.ok) throw new Error(json.message ?? `Error ${r.status}`);
      setReport(json as Report);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const pick = (list: FileList | null) => { setFiles(list ? [...list] : []); setReport(null); setError(null); };
  const hasErrors = report?.findings.some((f) => f.level === 'error');
  const [before, after] = t('imp.drop').split(/<u>.*<\/u>/);
  const linkWord = /<u>(.*)<\/u>/.exec(t('imp.drop'))?.[1] ?? '';

  return (
    <>
      <p className="muted lead">{t('imp.lead')}</p>

      <div className="panel" style={{ margin: 0, maxWidth: 'none' }}>
        <label className="drop" htmlFor="xlsx" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files); }}>
          {files.length ? files.map((f) => f.name).join(' · ') : <>{before}<u>{linkWord}</u>{after}</>}
          <input id="xlsx" type="file" accept=".xlsx" multiple hidden onChange={(e) => pick(e.target.files)} />
        </label>
        <div className="row">
          <button className="btn" disabled={!files.length || !!busy} onClick={() => run(false)}>{busy === 'dry' ? t('imp.checking') : t('imp.dry')}</button>
          <button className="btn primary" disabled={!report?.dryRun || !!busy || hasErrors} onClick={() => run(true)}>{busy === 'apply' ? t('imp.applying') : t('imp.apply')}</button>
          {report && !report.dryRun && <span className="pill pos">{t('imp.applied')}</span>}
          {hasErrors && <span className="muted">{t('imp.fixFirst')}</span>}
        </div>
        {error && <p className="err" role="alert">{error}</p>}
      </div>

      {report && (
        <>
          <div className="panel" style={{ margin: 0, maxWidth: 'none' }}>
            <h2>{report.dryRun ? t('imp.dryTitle') : t('imp.result')}</h2>
            <p className="muted" style={{ margin: 0 }}>{report.sources.map((s) => t('imp.rows', { y: short(s.name), n: s.rows })).join(' · ')}</p>
            <div className="tablewrap">
              <table>
                <thead><tr><th>{t('imp.col.area')}</th><th className="r">{t('imp.col.new')}</th><th className="r">{t('imp.col.changed')}</th><th className="r">{t('imp.col.unchanged')}</th></tr></thead>
                <tbody>
                  {COUNT_KEYS.filter((k) => report.counts[k]).map((k) => {
                    const c = report.counts[k]!;
                    return <tr key={k}><td>{t(`imp.c.${k}`)}</td><td className="r num">{c.created}</td><td className="r num">{c.updated}</td><td className="r num">{c.unchanged}</td></tr>;
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="panel" style={{ margin: 0, maxWidth: 'none' }}>
            <h2>{t('imp.findings', { n: report.findings.length })}</h2>
            {report.findings.length === 0 ? <p className="muted">{t('common.none')}</p> : (
              <ul className="findings">
                {(['error', 'warn', 'info'] as const).flatMap((lvl) => report.findings.filter((f) => f.level === lvl)).map((f, i) => (
                  <li key={i}>
                    <span className={`pill ${TONE[f.level]}`}>{t(`imp.level.${f.level}`)}</span>
                    <div>
                      <div>{findingText(f)}</div>
                      <div className="muted small">{f.refs.slice(0, 6).map((r) => t('imp.row', { y: short(r.source), n: r.row })).join(', ')}{f.refs.length > 6 ? t('imp.more', { n: f.refs.length - 6 }) : ''}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </>
  );
}
